import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import Button from "../../../components/element/button";
import Col from "../../../components/element/col";
import Icon from "../../../components/element/icon";
import Input from "../../../components/element/input";
import PhoneInput from "../../../components/common/phone.input";
import Row from "../../../components/element/row";
import { scanGeran } from "../../../helpers/geran.scan";
import { handleCatchError } from "../../../helpers/handle.catch.error";
import {
  DEFAULT_PHONE_COUNTRY,
  isValidMobile,
  toE164,
} from "../../../helpers/phone";
import { showToast } from "../../../helpers/show.toast";
import { createConsign } from "../../../redux/reducer/consign.reducer";
import useRequireAuth from "../../../components/hook/use.require.auth";
import GeranGuideModal from "../modals/geran.guide.modal";
import { useNavigate } from "react-router-dom";

const estimateForm = {
  odometer: "",
  contact_number: "",
  contact_number_country: DEFAULT_PHONE_COUNTRY,
  email: "",
  // remark: "Get Quote Online",
  car_plate: "",
  method: "get_quote_online",
  geran: null,
  car_photo: null,
  sold_with_car_plate: "no",
};

const estimateSchema = Yup.object().shape({
  odometer: Yup.string().required("Odometer is required"),
  contact_number: Yup.string()
    .required("Contact no is required")
    .test("mobile", "Please enter a valid phone number", function (value) {
      return isValidMobile(value, this.parent.contact_number_country);
    }),
  email: Yup.string()
    .email("Please enter a valid email address")
    .required("Email is required"),
  car_plate: Yup.string().required("Car plate is required"),
  geran: Yup.mixed().required("One(1) geran is required"),
  car_photo: Yup.mixed().required("One(1) car photo is required"),
});

function UploadField({
  accept,
  errors,
  inputRef,
  label,
  name,
  setFieldValue,
  values,
  isRequired,
  onRequestUpload,
  onFileSelected,
  notice,
}) {
  const selectFile = (file) => {
    if (!file) return;

    setFieldValue(name, file);
    onFileSelected?.(file);
  };

  const { t } = useTranslation();
  const fileName = values[name]?.name || t("Upload File");
  const fileType = values[name]?.type || "";

  return (
    <Col
      className={`_col consign-field consign-upload-field consign-${name}-field`}
    >
      <label
        className="_label field-label"
        htmlFor={`consign-${name}`}
        onClick={(event) => {
          if (onRequestUpload) {
            event.preventDefault();
            onRequestUpload();
          }
        }}
      >
        {t(label)} {isRequired && <span>*</span>}
      </label>
      <input
        id={`consign-${name}`}
        ref={inputRef}
        hidden={Boolean(onRequestUpload)}
        type="file"
        name={name}
        accept={accept}
        className="upload-input"
        onChange={(event) => selectFile(event.currentTarget.files?.[0])}
      />
      <button
        type="button"
        className="purchase-upload-box"
        aria-label={onRequestUpload ? t("Upload geran") : undefined}
        onClick={() =>
          onRequestUpload ? onRequestUpload() : inputRef.current?.click()
        }
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          event.stopPropagation();
          selectFile(event.dataTransfer.files?.[0]);
        }}
      >
        <Icon
          icon={
            fileType
              ? fileType === "application/pdf"
                ? "ant-design:file-pdf-outlined"
                : "ant-design:file-image-outlined"
              : accept.includes("pdf")
                ? "material-symbols:upload-rounded"
                : "uil:camera"
          }
        />
        <span className="purchase-upload-copy">
          <span className="_label purchase-upload-name">{fileName}</span>
          {!values[name]?.name && (
            <small className="accept-file-label">
              {t(accept.includes("pdf") ? "JPG, PNG or PDF" : "JPG or PNG")}
            </small>
          )}
        </span>
      </button>
      {errors[name] && <Row className="element _errors">{t(errors[name])}</Row>}
      {notice}
    </Col>
  );
}

// What the in-browser read of the geran photo found. It only advises: a
// customer can keep a photo we could not read, knowing it slows verification.
function GeranScanNotice({ status, onRetake, onKeep }) {
  const { t } = useTranslation();

  if (status === "checking") {
    return (
      <p className="geran-scan-note" role="status">
        <Icon icon="svg-spinners:ring-resize" />
        {t("Checking your geran photo...")}
      </p>
    );
  }

  if (status === "readable") {
    return (
      <p className="geran-scan-note is-readable" role="status">
        <Icon icon="solar:check-circle-linear" />
        {t("Your geran photo looks clear.")}
      </p>
    );
  }

  if (status !== "unreadable") return null;

  return (
    <Col className="_col geran-scan-warning" role="alert">
      <p className="_label geran-scan-title">
        <Icon icon="solar:danger-triangle-linear" />
        {t("We couldn't read this geran clearly")}
      </p>
      <p className="_label geran-scan-copy">
        {t(
          "A clearer photo is approved faster. With this one, information might not be accurate.",
        )}
      </p>
      <Row className="_row geran-scan-actions">
        <button type="button" className="geran-scan-retake" onClick={onRetake}>
          {t("Upload again")}
        </button>
        <button type="button" className="geran-scan-keep" onClick={onKeep}>
          {t("Continue anyway")}
        </button>
      </Row>
    </Col>
  );
}

export default function ConsignEstimateForm({ onSuccess }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const geranRef = useRef(null);
  const [isGeranGuideOpen, setGeranGuideOpen] = useState(false);
  // idle | checking | readable | unreadable | accepted
  const [geranScan, setGeranScan] = useState("idle");
  const geranScanRef = useRef(0);
  const carPhotoRef = useRef(null);
  const { consignLoading } = useSelector((state) => state.common);
  const { isOpenLoginModal } = useSelector((state) => state.modal);
  const { isAuthenticated, requireAuth } = useRequireAuth();
  // The submission is held here rather than in redux: it carries the uploaded
  // files, and it is this form that has to run it again once they are signed in.
  const submitFormRef = useRef(null);
  const pendingSubmitRef = useRef(false);
  const loginWasOpenRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || !pendingSubmitRef.current) return;

    pendingSubmitRef.current = false;
    // Submits again with the values still in the form, so they do not have to
    // fill it in a second time.
    submitFormRef.current?.();
  }, [isAuthenticated]);

  useEffect(() => {
    // Closed the login without signing in - drop the pending submission so a
    // later sign-in elsewhere does not submit this form behind their back.
    if (loginWasOpenRef.current && !isOpenLoginModal && !isAuthenticated) {
      pendingSubmitRef.current = false;
    }

    loginWasOpenRef.current = isOpenLoginModal;
  }, [isAuthenticated, isOpenLoginModal]);

  useEffect(
    () => () => {
      geranScanRef.current += 1;
    },
    [],
  );

  // Reads the photo in the browser to catch a blurry or cropped geran before
  // it is sent. Only the latest upload's result is shown.
  const checkGeran = (file) => {
    const scanId = ++geranScanRef.current;

    setGeranScan("checking");
    scanGeran(file).then(({ status }) => {
      if (scanId !== geranScanRef.current) return;
      setGeranScan(status === "skipped" ? "idle" : status);
    });
  };

  const handleSubmit = (
    values,
    { resetForm, setFieldError, setSubmitting },
  ) => {
    if (!requireAuth()) {
      pendingSubmitRef.current = true;
      setSubmitting(false);
      return;
    }

    const formData = new FormData();

    Object.entries(values).forEach(([key, value]) => {
      if (key === "contact_number_country") return;

      formData.append(
        key,
        key === "contact_number"
          ? toE164(value, values.contact_number_country)
          : value,
      );
    });

    dispatch(createConsign(formData))
      .unwrap()
      .then(() => {
        resetForm();
        setGeranScan("idle");
        onSuccess?.();
        showToast(
          "Request consignment successful!",
          "Thank you for your consignment submission. Our agent will contact you shortly.",
          "success",
        );
        navigate("/consignments");
      })
      .catch((error) => {
        handleCatchError(error, setFieldError);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <Formik
      initialValues={estimateForm}
      validationSchema={estimateSchema}
      onSubmit={handleSubmit}
    >
      {({ errors, isSubmitting, setFieldValue, submitForm, values }) => {
        submitFormRef.current = submitForm;

        return (
          <Form className="consign-estimate-form">
            {isGeranGuideOpen && (
              <GeranGuideModal
                onClose={() => setGeranGuideOpen(false)}
                onUpload={() => {
                  setGeranGuideOpen(false);
                  geranRef.current?.click();
                }}
              />
            )}
            <Col className="_col consign-field method-field">
              <p className="_label field-label">
                {t("How We Quote for You")} <span>*</span>
              </p>
              <Row className="_row method-list">
                <button
                  type="button"
                  className={`_button method-option ${
                    values.method === "get_quote_online" ? "active" : ""
                  }`}
                  onClick={() => setFieldValue("method", "get_quote_online")}
                >
                  <span className="_label method-title">
                    {t("Get Quote Online")}
                  </span>
                  <span className="_label method-subtitle">
                    {t("Get within 24 hours")}
                  </span>
                </button>
                <button
                  type="button"
                  className={`_button method-option ${
                    values.method === "transparent_drop_off" ? "active" : ""
                  }`}
                  onClick={() =>
                    setFieldValue("method", "transparent_drop_off")
                  }
                >
                  <span className="_label method-title">
                    {t("Transparent Drop-off")}
                  </span>
                  <span className="_label method-subtitle">
                    {t("Inspection at workshop")}
                  </span>
                </button>
              </Row>
            </Col>
            <Row className="consign-field-row consign-upload-single">
              <UploadField
                accept="image/*,.pdf"
                errors={errors}
                inputRef={geranRef}
                label="Geran"
                name="geran"
                onRequestUpload={() => {
                  // The guide is a first-time primer. Once a geran is on file
                  // the button is a replace action, so it opens the picker.
                  if (values.geran) {
                    geranRef.current?.click();
                    return;
                  }

                  setGeranGuideOpen(true);
                }}
                onFileSelected={checkGeran}
                notice={
                  <GeranScanNotice
                    status={geranScan}
                    onRetake={() => geranRef.current?.click()}
                    onKeep={() => setGeranScan("accepted")}
                  />
                }
                setFieldValue={setFieldValue}
                values={values}
                isRequired={true}
              />
            </Row>
            <Row className="consign-field-row consign-upload-single">
              <UploadField
                accept="image/*"
                errors={errors}
                inputRef={carPhotoRef}
                label="Car Photo"
                name="car_photo"
                setFieldValue={setFieldValue}
                values={values}
                isRequired={true}
              />
            </Row>
            <Row className="consign-field-row consign-upload-single">
              <PhoneInput
                className="consign-field"
                label={t("Contact Number")}
                name="contact_number"
                isRequired={true}
              />
            </Row>
            <Row className="consign-field-row consign-upload-single">
              <Input
                className="consign-field"
                label={t("Email")}
                name="email"
                placeholder="e.g info@dcar.com"
                type="email"
                isRequired={true}
              />
            </Row>
            <Row className="consign-field-row">
              <Input
                className="consign-field"
                label={t("ODO Meter (km)")}
                name="odometer"
                placeholder="e.g 65000"
                type="number"
                isRequired={true}
              />
              <Input
                className="consign-field"
                label={t("Car Plate")}
                name="car_plate"
                placeholder="e.g GOLD 1"
                isRequired={true}
              />
            </Row>
            <Col className="_col plate-option">
              <p className="_label field-label mb-2">
                {t("Sold with Your Car Plate")} <span>*</span>
              </p>
              <Row className="_row plate-choice-list">
                {["yes", "no"].map((value) => (
                  <button
                    type="button"
                    className={`_button plate-choice ${
                      values.sold_with_car_plate === value ? "active" : ""
                    } text-capitalize`}
                    key={String(value)}
                    onClick={() => {
                      setFieldValue("sold_with_car_plate", value);
                    }}
                  >
                    <span className="plate-radio" aria-hidden="true" />
                    {t(value)}
                  </button>
                ))}
              </Row>
            </Col>
            <Button
              type="submit"
              className="_button estimate-submit"
              disabled={consignLoading || isSubmitting}
            >
              <span className="_label submit-label">
                {t("Get My Estimate")}
              </span>
              <Icon icon="mingcute:send-plane-line" className="_svg submit" />
            </Button>
            <Row className="_row security-note security-note-inline">
              <Icon
                tag="span"
                icon="solar:lock-keyhole-linear"
                className="security-lock _svg security"
              />
              <span className="_label security-label">
                {t("Your information is safe and secure with us")}
              </span>
            </Row>
          </Form>
        );
      }}
    </Formik>
  );
}
